import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useOrderFormPersistence, OrderFormData } from "@/hooks/useOrderFormPersistence";
import { analytics } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { z } from "zod";
import { 
  Upload, 
  Loader2, 
  Check, 
  Video, 
  Image as ImageIcon, 
  Store, 
  Package,
  Palette,
  MessageSquare,
  Mail,
  Phone,
  Clock,
  Zap,
  FileText,
  Globe,
  Link as LinkIcon,
  Mic,
  Square,
  HelpCircle,
  ArrowLeft,
  Sparkles,
  ArrowRight,
  X,
  AtSign
} from "lucide-react";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import OrderSummary from "@/components/OrderSummary";
import { FloatingIcons } from "@/components/ui/floating-icons";
import { AnimatedSectionBadge } from "@/components/order/AnimatedSectionBadge";
import { PremiumPackCard } from "@/components/order/PremiumPackCard";
import { AnimatedProgressBar } from "@/components/order/AnimatedProgressBar";
import { PremiumSubmitButton } from "@/components/order/PremiumSubmitButton";
import { cn } from "@/lib/utils";

// Validation schemas
const emailSchema = z.string().email("Please enter a valid email address").max(255, "Email is too long");
const phoneSchema = z.string()
  .min(8, "Phone number must be at least 8 digits")
  .max(20, "Phone number is too long")
  .regex(/^[\d\s\-+()]+$/, "Please enter a valid phone number");
const textSchema = z.string().max(500, "Text is too long");
const longTextSchema = z.string().max(2000, "Text is too long");
const urlSchema = z.string().url("Please enter a valid URL").or(z.literal("")).optional();

type PackType = "starter" | "boost" | "launch" | "momentum";

interface Pack {
  id: PackType;
  name: string;
  videos: number;
  posters: number;
  price: number;
  popular?: boolean;
}

interface VideoConfig {
  language: string;
  format: string;
  goal: string;
  letUsChoose: boolean;
  vibe: string;
  customVibe: string;
  offerDetails: string;
  notes: string;
  // Goal-specific fields
  productUrl: string;
  productName: string;
  productFeatures: string;
  targetUrl: string;
  socialHandle: string;
  eventName: string;
  eventDate: string;
  eventLocation: string;
  eventTicketUrl: string;
  customGoal: string;
}

const packs: Pack[] = [
  { id: "starter", name: "Starter Spark", videos: 1, posters: 1, price: 75 },
  { id: "boost", name: "Boost Pack", videos: 3, posters: 3, price: 210 },
  { id: "launch", name: "Launch Pack", videos: 6, posters: 6, price: 375, popular: true },
  { id: "momentum", name: "Momentum Pack", videos: 10, posters: 10, price: 550 },
];

const addOns = [
  { id: "extra_posters", name: "Extra Poster Pack (3 posters)", price: 25 },
  { id: "whatsapp_support", name: "WhatsApp Support", price: 25 },
  { id: "logo_creation", name: "Logo Creation Service", price: 8 },
];

// Helper component for field info tooltips
const InfoTooltip = ({ text }: { text: string }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <HelpCircle className="w-4 h-4 text-muted-foreground hover:text-neon-cyan cursor-help ml-1 inline transition-colors" />
    </TooltipTrigger>
    <TooltipContent className="max-w-[250px] bg-black/90 backdrop-blur-xl border-white/10">
      <p className="text-sm">{text}</p>
    </TooltipContent>
  </Tooltip>
);

const Order = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loadSavedData, saveData, clearSavedData, showRestorationToast } = useOrderFormPersistence();
  const hasInitializedRef = useRef(false);
  
  // Pack and video configuration
  const [selectedPack, setSelectedPack] = useState<Pack>(packs[2]); // Default to Launch Pack
  const [videoConfigs, setVideoConfigs] = useState<VideoConfig[]>([]);
  
  // Main campaign goals
  const [mainGoal, setMainGoal] = useState("awareness");
  
  // Location
  const [googleMapsLink, setGoogleMapsLink] = useState("");
  
  // Assets state
  // Multi-image uploads (up to 4 each)
  const [logoFiles, setLogoFiles] = useState<File[]>([]);
  const [logoPreviews, setLogoPreviews] = useState<string[]>([]);
  const [storePhotos, setStorePhotos] = useState<File[]>([]);
  const [storePhotoPreviews, setStorePhotoPreviews] = useState<string[]>([]);
  const [productImages, setProductImages] = useState<File[]>([]);
  const [productImagePreviews, setProductImagePreviews] = useState<string[]>([]);
  const [referenceUrl, setReferenceUrl] = useState("");
  const [brandColor, setBrandColor] = useState("#e91e8c");
  const [businessWebsite, setBusinessWebsite] = useState("");
  const [socialMedia, setSocialMedia] = useState("");
  
  // Business details
  const [businessName, setBusinessName] = useState("");
  const [city, setCity] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [platforms, setPlatforms] = useState<string[]>(["tiktok", "instagram"]);
  const [callToAction, setCallToAction] = useState("");
  
  // Creative direction
  const [creativeGoal, setCreativeGoal] = useState("");
  const [vibe, setVibe] = useState("excited");
  const [inspiration, setInspiration] = useState("");
  
  // Voice note
  const [voiceNoteFile, setVoiceNoteFile] = useState<File | null>(null);
  const [voiceNoteUrl, setVoiceNoteUrl] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Contact & delivery
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [deliveryPreference, setDeliveryPreference] = useState("standard");
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [customNotes, setCustomNotes] = useState("");
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Restore saved form data on mount
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const packParam = searchParams.get("pack");
    const savedData = loadSavedData();

    if (savedData) {
      // URL param overrides saved pack
      if (packParam) {
        const pack = packs.find(p => p.id === packParam);
        if (pack) setSelectedPack(pack);
      } else if (savedData.selectedPackId) {
        const pack = packs.find(p => p.id === savedData.selectedPackId);
        if (pack) setSelectedPack(pack);
      }

      // Restore all other fields
      if (savedData.videoConfigs?.length > 0) setVideoConfigs(savedData.videoConfigs);
      if (savedData.mainGoal) setMainGoal(savedData.mainGoal);
      if (savedData.googleMapsLink) setGoogleMapsLink(savedData.googleMapsLink);
      if (savedData.referenceUrl) setReferenceUrl(savedData.referenceUrl);
      if (savedData.brandColor) setBrandColor(savedData.brandColor);
      if (savedData.businessWebsite) setBusinessWebsite(savedData.businessWebsite);
      if (savedData.socialMedia) setSocialMedia(savedData.socialMedia);
      if (savedData.businessName) setBusinessName(savedData.businessName);
      if (savedData.city) setCity(savedData.city);
      if (savedData.businessType) setBusinessType(savedData.businessType);
      if (savedData.targetAudience) setTargetAudience(savedData.targetAudience);
      if (savedData.platforms?.length > 0) setPlatforms(savedData.platforms);
      if (savedData.callToAction) setCallToAction(savedData.callToAction);
      if (savedData.creativeGoal) setCreativeGoal(savedData.creativeGoal);
      if (savedData.vibe) setVibe(savedData.vibe);
      if (savedData.inspiration) setInspiration(savedData.inspiration);
      if (savedData.email) setEmail(savedData.email);
      if (savedData.whatsapp) setWhatsapp(savedData.whatsapp);
      if (savedData.deliveryPreference) setDeliveryPreference(savedData.deliveryPreference);
      if (savedData.selectedAddOns?.length > 0) setSelectedAddOns(savedData.selectedAddOns);
      if (savedData.customNotes) setCustomNotes(savedData.customNotes);

      showRestorationToast();
    } else if (packParam) {
      const pack = packs.find(p => p.id === packParam);
      if (pack) setSelectedPack(pack);
    }
  }, [searchParams, loadSavedData, showRestorationToast]);
  
  // Initialize video configs based on selected pack (only if not restored)
  useEffect(() => {
    // Skip if we have restored configs that match the pack
    if (videoConfigs.length === selectedPack.videos) return;
    
    const configs: VideoConfig[] = [];
    for (let i = 0; i < selectedPack.videos; i++) {
      // Preserve existing config data if available
      const existingConfig = videoConfigs[i];
      configs.push(existingConfig || {
        language: "English",
        format: "9:16",
        goal: "",
        letUsChoose: false,
        vibe: "",
        customVibe: "",
        offerDetails: "",
        notes: "",
        productUrl: "",
        productName: "",
        productFeatures: "",
        targetUrl: "",
        socialHandle: "",
        eventName: "",
        eventDate: "",
        eventLocation: "",
        eventTicketUrl: "",
        customGoal: ""
      });
    }
    setVideoConfigs(configs);
  }, [selectedPack]);

  // Save form data on changes
  useEffect(() => {
    if (!hasInitializedRef.current) return;
    
    const formData: OrderFormData = {
      selectedPackId: selectedPack.id,
      videoConfigs,
      mainGoal,
      googleMapsLink,
      referenceUrl,
      brandColor,
      businessWebsite,
      socialMedia,
      businessName,
      city,
      businessType,
      targetAudience,
      platforms,
      callToAction,
      creativeGoal,
      vibe,
      inspiration,
      email,
      whatsapp,
      deliveryPreference,
      selectedAddOns,
      customNotes,
    };
    saveData(formData);
  }, [
    selectedPack, videoConfigs, mainGoal, googleMapsLink, referenceUrl,
    brandColor, businessWebsite, socialMedia, businessName, city,
    businessType, targetAudience, platforms, callToAction, creativeGoal,
    vibe, inspiration, email, whatsapp, deliveryPreference, selectedAddOns,
    customNotes, saveData
  ]);

  // Track order page view
  useEffect(() => {
    analytics.orderPageViewed(selectedPack.name);
  }, []);

  // Auto-sync mainGoal from first video's goal
  useEffect(() => {
    if (videoConfigs.length > 0) {
      setMainGoal(videoConfigs[0].goal);
    }
  }, [videoConfigs]);

  // Auto-select logo creation if no logo uploaded
  useEffect(() => {
    if (logoFiles.length === 0) {
      if (!selectedAddOns.includes("logo_creation")) {
        setSelectedAddOns(prev => [...prev, "logo_creation"]);
      }
    }
  }, [logoFiles]);

  // Multi-file upload handler (up to 4 per category)
  const handleMultiFileUpload = (files: FileList | null, type: "logo" | "store" | "product") => {
    if (!files) return;
    
    const currentCount = type === "logo" ? logoFiles.length : type === "store" ? storePhotos.length : productImages.length;
    const maxFiles = 4;
    const remaining = maxFiles - currentCount;
    
    if (remaining <= 0) {
      toast.error(`Maximum ${maxFiles} images allowed`);
      return;
    }

    const filesToAdd = Array.from(files).slice(0, remaining);
    
    filesToAdd.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const preview = reader.result as string;
        if (type === "logo") {
          setLogoFiles(prev => [...prev, file]);
          setLogoPreviews(prev => [...prev, preview]);
        } else if (type === "store") {
          setStorePhotos(prev => [...prev, file]);
          setStorePhotoPreviews(prev => [...prev, preview]);
        } else if (type === "product") {
          setProductImages(prev => [...prev, file]);
          setProductImagePreviews(prev => [...prev, preview]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove single image from multi-upload
  const removeImage = (index: number, type: "logo" | "store" | "product") => {
    if (type === "logo") {
      setLogoFiles(prev => prev.filter((_, i) => i !== index));
      setLogoPreviews(prev => prev.filter((_, i) => i !== index));
    } else if (type === "store") {
      setStorePhotos(prev => prev.filter((_, i) => i !== index));
      setStorePhotoPreviews(prev => prev.filter((_, i) => i !== index));
    } else if (type === "product") {
      setProductImages(prev => prev.filter((_, i) => i !== index));
      setProductImagePreviews(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handlePlatformToggle = (platform: string) => {
    setPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  const calculateTotal = () => {
    let total = selectedPack.price;
    selectedAddOns.forEach((addOnId) => {
      const addOn = addOns.find((a) => a.id === addOnId);
      if (addOn) total += addOn.price;
    });
    if (deliveryPreference === "express") total += 49;
    return total;
  };

  const getCompletedSections = () => {
    let completed = 1; // Pack selection counts as 1 completed
    
    // Step 2: Video Breakdown - count if user has configured goal and vibe for all videos
    const hasConfiguredVideos = videoConfigs.every(vc => 
      vc.goal && vc.goal.trim() !== "" && 
      (vc.letUsChoose === true || (vc.vibe && vc.vibe.trim() !== ""))
    );
    if (hasConfiguredVideos && videoConfigs.length > 0) completed++;
    
    // Step 3: Location (Google Maps link)
    if (googleMapsLink.trim()) completed++;
    
    // Step 4: Assets & Branding (any images uploaded)
    if (logoFiles.length > 0 || storePhotos.length > 0 || productImages.length > 0) completed++;
    
    // Step 5: Business Context (requires actual business info)
    if (businessName.trim() && city.trim()) completed++;
    
    // Step 6: Final Notes - only check user input, not defaults
    if (customNotes.trim() || voiceNoteFile || audioBlob) completed++;
    
    // Step 7: Contact & Delivery (all required fields)
    if (email.trim() && whatsapp.trim() && agreedToTerms) completed++;
    
    return completed;
  };

  // Voice recording handlers
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(audioBlob);
        const audioFile = new File([audioBlob], `voice-note-${Date.now()}.webm`, { type: "audio/webm" });
        setVoiceNoteFile(audioFile);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 60) {
            stopRecording();
            return 60;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      toast.error("Could not access microphone");
      console.error("Recording error:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleVoiceNoteUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Audio file must be less than 10MB");
        return;
      }
      setVoiceNoteFile(file);
      setAudioBlob(file);
    }
  };

  const handleSubmit = async () => {
    // Validate video configurations - Goal and Vibe are required
    for (let i = 0; i < videoConfigs.length; i++) {
      const config = videoConfigs[i];
      
      // Goal is required
      if (!config.goal || config.goal.trim() === "") {
        toast.error(`Please select a goal for Video ${i + 1}`);
        return;
      }
      
      // Vibe is required (either selected vibe or "let us choose")
      if (!config.letUsChoose && (!config.vibe || config.vibe.trim() === "")) {
        toast.error(`Please select a vibe for Video ${i + 1} or choose "Let us choose for you"`);
        return;
      }
      
      // If custom vibe is selected, the custom text is required
      if (config.vibe === "other" && !config.letUsChoose && (!config.customVibe || config.customVibe.trim() === "")) {
        toast.error(`Please describe your custom vibe for Video ${i + 1}`);
        return;
      }
    }
    
    // Validation with zod
    const businessNameResult = textSchema.safeParse(businessName);
    if (!businessName.trim()) {
      toast.error("Please fill in business name");
      return;
    }
    if (!businessNameResult.success) {
      toast.error(businessNameResult.error.errors[0]?.message || "Invalid business name");
      return;
    }
    
    const cityResult = textSchema.safeParse(city);
    if (!city.trim()) {
      toast.error("Please fill in city");
      return;
    }
    if (!cityResult.success) {
      toast.error(cityResult.error.errors[0]?.message || "Invalid city");
      return;
    }
    
    // Email validation
    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) {
      toast.error(emailResult.error.errors[0]?.message || "Please enter a valid email");
      return;
    }
    
    // Phone validation
    const phoneResult = phoneSchema.safeParse(whatsapp);
    if (!phoneResult.success) {
      toast.error(phoneResult.error.errors[0]?.message || "Please enter a valid WhatsApp number");
      return;
    }
    
    // Optional URL validations
    if (googleMapsLink && googleMapsLink.trim()) {
      const mapsResult = urlSchema.safeParse(googleMapsLink);
      if (!mapsResult.success) {
        toast.error("Please enter a valid Google Maps URL");
        return;
      }
    }
    
    if (referenceUrl && referenceUrl.trim()) {
      const refResult = urlSchema.safeParse(referenceUrl);
      if (!refResult.success) {
        toast.error("Please enter a valid reference URL");
        return;
      }
    }
    
    // Custom notes validation
    if (customNotes) {
      const notesResult = longTextSchema.safeParse(customNotes);
      if (!notesResult.success) {
        toast.error(notesResult.error.errors[0]?.message || "Notes are too long");
        return;
      }
    }
    
    if (!agreedToTerms) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload assets to storage
      let logoUrls: string[] = [];
      let storePhotoUrls: string[] = [];
      let productImageUrls: string[] = [];
      let voiceNoteUploadUrl = "";

      // Upload logo files
      for (const file of logoFiles) {
        const logoPath = `logos/${Date.now()}-${file.name}`;
        const { error: logoError } = await supabase.storage
          .from("order-assets")
          .upload(logoPath, file);
        
        if (logoError) throw logoError;
        
        const { data: logoData } = supabase.storage
          .from("order-assets")
          .getPublicUrl(logoPath);
        logoUrls.push(logoData.publicUrl);
      }

      // Upload store photos
      for (const file of storePhotos) {
        const storePath = `stores/${Date.now()}-${file.name}`;
        const { error: storeError } = await supabase.storage
          .from("order-assets")
          .upload(storePath, file);
        
        if (storeError) throw storeError;
        
        const { data: storeData } = supabase.storage
          .from("order-assets")
          .getPublicUrl(storePath);
        storePhotoUrls.push(storeData.publicUrl);
      }

      // Upload product images
      for (const file of productImages) {
        const productPath = `products/${Date.now()}-${file.name}`;
        const { error: productError } = await supabase.storage
          .from("order-assets")
          .upload(productPath, file);
        
        if (productError) throw productError;
        
        const { data: productData } = supabase.storage
          .from("order-assets")
          .getPublicUrl(productPath);
        productImageUrls.push(productData.publicUrl);
      }

      if (voiceNoteFile) {
        const voicePath = `voice-notes/${Date.now()}-${voiceNoteFile.name}`;
        const { error: voiceError } = await supabase.storage
          .from("order-assets")
          .upload(voicePath, voiceNoteFile);
        
        if (voiceError) throw voiceError;
        
        const { data: voiceData } = supabase.storage
          .from("order-assets")
          .getPublicUrl(voicePath);
        voiceNoteUploadUrl = voiceData.publicUrl;
      }

      // Track checkout started
      analytics.checkoutStarted(
        selectedPack.name,
        calculateTotal(),
        'EUR',
        selectedPack.videos
      );

      // Create checkout session
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          packType: selectedPack.id,
          videoConfigs,
          mainGoal,
          googleMapsLink,
          logoUrls,
          storePhotoUrls,
          productImageUrls,
          referenceUrl,
          brandColor,
          businessName,
          city,
          businessType,
          targetAudience,
          platforms,
          callToAction,
          vibe,
          creativeGoal,
          inspiration,
          voiceNoteUrl: voiceNoteUploadUrl,
          email,
          whatsapp,
          deliveryPreference,
          addOns: selectedAddOns,
          customNotes,
          businessWebsite,
          socialMedia,
        },
      });

      if (error) throw error;

      // Redirect to Stripe checkout
      if (data?.url) {
        clearSavedData(); // Clear saved form data after successful checkout
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error: any) {
      console.error("Order submission error:", error);
      toast.error(error.message || "Failed to create order. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen gradient-mesh relative overflow-hidden">
        {/* Minimal Order Header */}
        <header className="fixed top-0 left-0 right-0 z-50 glass-morphism border-b border-white/10">
          <div className="container mx-auto px-4 h-14 flex items-center justify-between">
            <a href="/" className="flex items-center gap-2 group">
              <Logo className="h-7 w-auto" />
            </a>
            <a 
              href="/" 
              className="text-sm text-muted-foreground hover:text-white transition-colors flex items-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
            </a>
          </div>
        </header>
      
      {/* Floating Background Icons */}
      <FloatingIcons
        desktopIcons={[
          { emoji: "⛽", top: "10%", left: "5%", delay: "0s" },
          { emoji: "🚀", top: "20%", right: "8%", delay: "2s" },
          { emoji: "🔥", top: "40%", left: "3%", delay: "4s" },
          { emoji: "💨", top: "60%", right: "5%", delay: "1s" },
          { emoji: "✨", top: "75%", left: "7%", delay: "3s" },
          { emoji: "⚡", top: "85%", right: "10%", delay: "5s" },
        ]}
        mobileIcons={[
          { emoji: "⛽", top: "8%", left: "3%", delay: "0s" },
          { emoji: "🚀", top: "30%", right: "5%", delay: "2s" },
          { emoji: "🔥", top: "60%", left: "4%", delay: "3s" },
          { emoji: "💨", top: "80%", right: "6%", delay: "1s" },
        ]}
      />
      
      <div className="container mx-auto px-4 pt-20 pb-32 md:pb-8 relative z-10">
        {/* Progress Bar */}
        <div className="mb-8 animate-fade-in">
          <AnimatedProgressBar 
            completedSections={getCompletedSections()} 
            totalSections={7} 
          />
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-12">
            {/* Section 1: Choose Your Pack */}
            <section className={cn(
              "glass-morphism rounded-xl p-6 border-2 transition-all duration-500",
              "border-neon-purple/30 hover:border-neon-purple/50",
              "animate-section-reveal"
            )}>
              <div className="flex items-center gap-3 mb-6">
                <AnimatedSectionBadge 
                  number={1} 
                  isComplete={true}
                  isActive={true}
                />
                <h2 className="text-2xl font-heading font-bold bg-gradient-to-r from-white via-neon-purple to-white animate-text-shimmer bg-[length:200%_100%] bg-clip-text text-transparent">
                  ⛽ Choose Your Fuel
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {packs.map((pack, index) => (
                  <PremiumPackCard
                    key={pack.id}
                    pack={pack}
                    isSelected={selectedPack.id === pack.id}
                    onSelect={() => setSelectedPack(pack)}
                    index={index}
                  />
                ))}
              </div>
            </section>

            {/* Section 2: Video Breakdown */}
            <section className={cn(
              "glass-morphism rounded-xl p-6 border-2 transition-all duration-500",
              "border-neon-purple/30 hover:border-neon-purple/50",
              "animate-section-reveal"
            )} style={{ animationDelay: "100ms" }}>
              <div className="flex items-center gap-3 mb-6">
                <AnimatedSectionBadge 
                  number={2} 
                  isComplete={videoConfigs.length > 0}
                />
                <h2 className="text-2xl font-heading font-bold bg-gradient-to-r from-white via-neon-purple to-white animate-text-shimmer bg-[length:200%_100%] bg-clip-text text-transparent">🎬 Fuel Specs</h2>
              </div>

              <div className="space-y-4">
                {videoConfigs.map((config, index) => (
                  <div key={index} className="border border-neon-purple/20 rounded-lg p-4 bg-white/5 backdrop-blur-sm hover:border-neon-purple/40 transition-all">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="font-bold text-neon-cyan">Video {index + 1}</span>
                      <span className="text-xs px-2 py-1 rounded-full bg-neon-purple/20 text-neon-purple border border-neon-purple/30">
                        📐 9:16 Vertical
                      </span>
                    </div>
                    
                    {/* Language & Goal Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label htmlFor={`language-${index}`}>
                          🌐 Language
                          <InfoTooltip text="Primary language for voiceover and text overlays in this video" />
                        </Label>
                        <Select
                          value={config.language}
                          onValueChange={(value) => {
                            const newConfigs = [...videoConfigs];
                            newConfigs[index].language = value;
                            setVideoConfigs(newConfigs);
                          }}
                        >
                          <SelectTrigger id={`language-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="English">🇬🇧 English</SelectItem>
                            <SelectItem value="German">🇩🇪 German</SelectItem>
                            <SelectItem value="French">🇫🇷 French</SelectItem>
                            <SelectItem value="Spanish">🇪🇸 Spanish</SelectItem>
                            <SelectItem value="Italian">🇮🇹 Italian</SelectItem>
                            <SelectItem value="Portuguese">🇵🇹 Portuguese</SelectItem>
                            <SelectItem value="Dutch">🇳🇱 Dutch</SelectItem>
                            <SelectItem value="Arabic">🇸🇦 Arabic</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor={`goal-${index}`}>
                          🎯 Goal for This Video <span className="text-red-500">*</span>
                          <InfoTooltip text="What should this specific video achieve for your business?" />
                        </Label>
                        <Select
                          value={config.goal}
                          onValueChange={(value) => {
                            const newConfigs = [...videoConfigs];
                            newConfigs[index].goal = value;
                            setVideoConfigs(newConfigs);
                          }}
                        >
                          <SelectTrigger id={`goal-${index}`}>
                            <SelectValue placeholder="Select a goal..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="awareness">✨ Awareness - Build brand recognition</SelectItem>
                            <SelectItem value="engagement">💬 Engagement - Boost interactions</SelectItem>
                            <SelectItem value="sales">💰 Sales - Drive purchases</SelectItem>
                            <SelectItem value="leads">🎯 Leads - Generate inquiries</SelectItem>
                            <SelectItem value="foot-traffic">📍 Foot Traffic - Drive store visits</SelectItem>
                            <SelectItem value="calls-bookings">📞 Calls & Bookings - Get appointments</SelectItem>
                            <SelectItem value="website-traffic">🔗 Website Traffic - Drive clicks</SelectItem>
                            <SelectItem value="followers">👥 Followers Growth - Build following</SelectItem>
                            <SelectItem value="offer">🎁 Promote an Offer - Feature a deal</SelectItem>
                            <SelectItem value="product-launch">🆕 Product Launch - Introduce new product</SelectItem>
                            <SelectItem value="event">📣 Event Promotion - Promote an event</SelectItem>
                            <SelectItem value="trust">🏆 Build Trust - Showcase credibility</SelectItem>
                            <SelectItem value="general-boost">🚀 General Boost - Overall growth</SelectItem>
                            <SelectItem value="other">✍️ Other - Describe your goal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Vibe Selector - Visible by default */}
                    <div className={`mb-4 transition-all duration-300 ${config.letUsChoose ? 'opacity-50 hover:opacity-75' : ''}`}>
                      <Label className="mb-2 block">
                        ⚡ Vibe <span className="text-red-500">*</span>
                        <InfoTooltip text="Choose the emotional tone for this video" />
                      </Label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {[
                          { value: "excited", emoji: "🔥", label: "Excited" },
                          { value: "smooth", emoji: "😎", label: "Smooth" },
                          { value: "luxury", emoji: "👑", label: "Luxury" },
                          { value: "funny", emoji: "😂", label: "Funny" },
                          { value: "professional", emoji: "💼", label: "Professional" },
                          { value: "casual", emoji: "👋", label: "Casual" },
                          { value: "bold", emoji: "⚡", label: "Bold" },
                          { value: "inspirational", emoji: "✨", label: "Inspirational" },
                          { value: "minimalist", emoji: "⚪", label: "Minimalist" },
                          { value: "playful", emoji: "🎮", label: "Playful" },
                          { value: "dramatic", emoji: "🎭", label: "Dramatic" },
                          { value: "other", emoji: "✏️", label: "Custom" }
                        ].map((vibeOption) => (
                          <button
                            key={vibeOption.value}
                            type="button"
                            onClick={() => {
                              const newConfigs = [...videoConfigs];
                              newConfigs[index].vibe = vibeOption.value;
                              newConfigs[index].letUsChoose = false; // Auto-uncheck "Let Us Choose" when selecting a vibe
                              setVideoConfigs(newConfigs);
                            }}
                            className={`p-3 rounded-lg border-2 transition-all text-center ${
                              config.vibe === vibeOption.value
                                ? "border-neon-pink bg-neon-pink/10 shadow-lg shadow-neon-pink/20 scale-105"
                                : "border-border/40 bg-card/30 hover:border-neon-purple/40"
                            }`}
                          >
                            <div className="text-2xl mb-1">{vibeOption.emoji}</div>
                            <div className="text-xs font-medium">{vibeOption.label}</div>
                          </button>
                        ))}
                      </div>
                      
                      {/* Custom Vibe Input */}
                      {config.vibe === "other" && !config.letUsChoose && (
                        <div className="mt-3 p-3 rounded-lg border border-neon-cyan/30 bg-neon-cyan/5 animate-fade-in">
                          <Label htmlFor={`custom-vibe-${index}`} className="text-sm">
                            ✏️ Describe Your Custom Vibe
                            <InfoTooltip text="Tell us the specific mood or tone you want for this video" />
                          </Label>
                          <Input
                            id={`custom-vibe-${index}`}
                            type="text"
                            placeholder="e.g., Mysterious, Nostalgic, High-energy EDM, Calm meditation..."
                            value={config.customVibe}
                            onChange={(e) => {
                              const newConfigs = [...videoConfigs];
                              newConfigs[index].customVibe = e.target.value;
                              setVideoConfigs(newConfigs);
                            }}
                            className="mt-2"
                          />
                        </div>
                      )}
                    </div>

                    {/* Let Us Choose - Helpful Fallback Checkbox */}
                    <div className="mb-4">
                      <label 
                        className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          config.letUsChoose 
                            ? 'border-neon-cyan bg-neon-cyan/10 shadow-lg shadow-neon-cyan/20' 
                            : 'border-border/40 bg-card/30 hover:border-neon-cyan/40'
                        }`}
                      >
                        <Checkbox
                          checked={config.letUsChoose}
                          onCheckedChange={(checked) => {
                            const newConfigs = [...videoConfigs];
                            newConfigs[index].letUsChoose = checked as boolean;
                            if (checked) {
                              newConfigs[index].vibe = ""; // Clear vibe when "Let Us Choose" is checked
                              newConfigs[index].customVibe = "";
                            }
                            setVideoConfigs(newConfigs);
                          }}
                          className="mt-1 data-[state=checked]:bg-neon-cyan data-[state=checked]:border-neon-cyan"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-base">🤔</span>
                            <span className="font-medium">Not sure? Let us choose for you</span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-neon-green/20 text-neon-green border border-neon-green/30 font-bold">FREE</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            We'll analyze your business & goals to craft the perfect vibe, concept & script
                          </p>
                          
                          {/* Expanded confidence message when checked */}
                          {config.letUsChoose && (
                            <div className="mt-3 pt-3 border-t border-neon-cyan/30 animate-fade-in">
                              <p className="text-sm text-neon-cyan font-medium mb-2">🧠 We've got this covered!</p>
                              <ul className="text-xs text-muted-foreground space-y-1">
                                <li className="flex items-center gap-2">
                                  <Check className="w-3 h-3 text-neon-green" />
                                  We'll analyze your business, ICP, and goals
                                </li>
                                <li className="flex items-center gap-2">
                                  <Check className="w-3 h-3 text-neon-green" />
                                  Craft the perfect vibe, concept & script
                                </li>
                                <li className="flex items-center gap-2">
                                  <Check className="w-3 h-3 text-neon-green" />
                                  <span className="text-neon-purple/80">8+ years marketing + 2 years AI expertise included</span>
                                </li>
                              </ul>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>

                    {/* Conditional Fields Based on Goal */}
                    
                    {/* Offer Details */}
                    {config.goal === "offer" && (
                      <div className="p-3 rounded-lg border border-neon-pink/30 bg-neon-pink/5 animate-fade-in">
                        <Label htmlFor={`offer-details-${index}`} className="text-sm">
                          🎁 Offer Details
                          <InfoTooltip text="Describe the specific offer for this video" />
                        </Label>
                        <Input
                          id={`offer-details-${index}`}
                          type="text"
                          placeholder="e.g., 20% off, BOGO, Free trial..."
                          value={config.offerDetails}
                          onChange={(e) => {
                            const newConfigs = [...videoConfigs];
                            newConfigs[index].offerDetails = e.target.value;
                            setVideoConfigs(newConfigs);
                          }}
                          className="mt-2"
                        />
                      </div>
                    )}

                    {/* Sales - Product/Service URL */}
                    {config.goal === "sales" && (
                      <div className="p-3 rounded-lg border border-neon-green/30 bg-neon-green/5 animate-fade-in">
                        <Label htmlFor={`product-url-${index}`} className="text-sm">
                          🛒 Product/Service URL
                          <InfoTooltip text="Where should viewers go to purchase? We'll include this in the CTA" />
                        </Label>
                        <Input
                          id={`product-url-${index}`}
                          type="url"
                          placeholder="https://yourstore.com/product..."
                          value={config.productUrl}
                          onChange={(e) => {
                            const newConfigs = [...videoConfigs];
                            newConfigs[index].productUrl = e.target.value;
                            setVideoConfigs(newConfigs);
                          }}
                          className="mt-2"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Link to your product page, shop, or checkout</p>
                      </div>
                    )}

                    {/* Leads - Landing Page URL */}
                    {config.goal === "leads" && (
                      <div className="p-3 rounded-lg border border-neon-cyan/30 bg-neon-cyan/5 animate-fade-in">
                        <Label htmlFor={`target-url-${index}`} className="text-sm">
                          📝 Sign-up / Landing Page URL
                          <InfoTooltip text="Where should potential leads go to sign up or learn more?" />
                        </Label>
                        <Input
                          id={`target-url-${index}`}
                          type="url"
                          placeholder="https://yoursite.com/signup..."
                          value={config.targetUrl}
                          onChange={(e) => {
                            const newConfigs = [...videoConfigs];
                            newConfigs[index].targetUrl = e.target.value;
                            setVideoConfigs(newConfigs);
                          }}
                          className="mt-2"
                        />
                      </div>
                    )}

                    {/* Website Traffic - Target URL */}
                    {config.goal === "website-traffic" && (
                      <div className="p-3 rounded-lg border border-neon-purple/30 bg-neon-purple/5 animate-fade-in">
                        <Label htmlFor={`website-url-${index}`} className="text-sm">
                          🔗 Target Page URL
                          <InfoTooltip text="Which page should we drive traffic to?" />
                        </Label>
                        <Input
                          id={`website-url-${index}`}
                          type="url"
                          placeholder="https://yoursite.com/page..."
                          value={config.targetUrl}
                          onChange={(e) => {
                            const newConfigs = [...videoConfigs];
                            newConfigs[index].targetUrl = e.target.value;
                            setVideoConfigs(newConfigs);
                          }}
                          className="mt-2"
                        />
                      </div>
                    )}

                    {/* Followers Growth - Social Handle */}
                    {config.goal === "followers" && (
                      <div className="p-3 rounded-lg border border-neon-pink/30 bg-neon-pink/5 animate-fade-in">
                        <Label htmlFor={`social-handle-${index}`} className="text-sm">
                          📱 Social Profile URL or Handle
                          <InfoTooltip text="Your social media profile to drive followers to" />
                        </Label>
                        <Input
                          id={`social-handle-${index}`}
                          type="text"
                          placeholder="@yourbusiness or https://instagram.com/yourbusiness"
                          value={config.socialHandle}
                          onChange={(e) => {
                            const newConfigs = [...videoConfigs];
                            newConfigs[index].socialHandle = e.target.value;
                            setVideoConfigs(newConfigs);
                          }}
                          className="mt-2"
                        />
                      </div>
                    )}

                    {/* Product Launch - Product Details */}
                    {config.goal === "product-launch" && (
                      <div className="p-3 rounded-lg border border-neon-cyan/30 bg-neon-cyan/5 animate-fade-in space-y-3">
                        <div>
                          <Label htmlFor={`product-name-${index}`} className="text-sm">
                            🆕 Product Name
                            <InfoTooltip text="Name of the new product you're launching" />
                          </Label>
                          <Input
                            id={`product-name-${index}`}
                            type="text"
                            placeholder="e.g., New Summer Collection, Premium Membership..."
                            value={config.productName}
                            onChange={(e) => {
                              const newConfigs = [...videoConfigs];
                              newConfigs[index].productName = e.target.value;
                              setVideoConfigs(newConfigs);
                            }}
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label htmlFor={`product-launch-url-${index}`} className="text-sm">
                            🔗 Product URL (Optional)
                          </Label>
                          <Input
                            id={`product-launch-url-${index}`}
                            type="url"
                            placeholder="https://yourstore.com/new-product"
                            value={config.productUrl}
                            onChange={(e) => {
                              const newConfigs = [...videoConfigs];
                              newConfigs[index].productUrl = e.target.value;
                              setVideoConfigs(newConfigs);
                            }}
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label htmlFor={`product-features-${index}`} className="text-sm">
                            ✨ Key Features / Selling Points
                            <InfoTooltip text="What makes this product special? 2-3 key benefits to highlight" />
                          </Label>
                          <Textarea
                            id={`product-features-${index}`}
                            placeholder="What makes this product special? List 2-3 key benefits..."
                            value={config.productFeatures}
                            onChange={(e) => {
                              const newConfigs = [...videoConfigs];
                              newConfigs[index].productFeatures = e.target.value;
                              setVideoConfigs(newConfigs);
                            }}
                            className="mt-2 min-h-[80px]"
                          />
                        </div>
                      </div>
                    )}

                    {/* Event Promotion - Event Details */}
                    {config.goal === "event" && (
                      <div className="p-3 rounded-lg border border-neon-purple/30 bg-neon-purple/5 animate-fade-in space-y-3">
                        <div>
                          <Label htmlFor={`event-name-${index}`} className="text-sm">
                            📣 Event Name
                            <InfoTooltip text="Name of your event or promotion" />
                          </Label>
                          <Input
                            id={`event-name-${index}`}
                            type="text"
                            placeholder="e.g., Summer Grand Opening, Live Music Night..."
                            value={config.eventName}
                            onChange={(e) => {
                              const newConfigs = [...videoConfigs];
                              newConfigs[index].eventName = e.target.value;
                              setVideoConfigs(newConfigs);
                            }}
                            className="mt-2"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor={`event-date-${index}`} className="text-sm">
                              📅 Event Date
                            </Label>
                            <Input
                              id={`event-date-${index}`}
                              type="date"
                              value={config.eventDate}
                              onChange={(e) => {
                                const newConfigs = [...videoConfigs];
                                newConfigs[index].eventDate = e.target.value;
                                setVideoConfigs(newConfigs);
                              }}
                              className="mt-2"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`event-location-${index}`} className="text-sm">
                              📍 Event Location
                            </Label>
                            <Input
                              id={`event-location-${index}`}
                              type="text"
                              placeholder="Your address or 'Online'"
                              value={config.eventLocation}
                              onChange={(e) => {
                                const newConfigs = [...videoConfigs];
                                newConfigs[index].eventLocation = e.target.value;
                                setVideoConfigs(newConfigs);
                              }}
                              className="mt-2"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor={`event-ticket-${index}`} className="text-sm">
                            🎟️ Ticket / RSVP Link (Optional)
                          </Label>
                          <Input
                            id={`event-ticket-${index}`}
                            type="url"
                            placeholder="https://eventbrite.com/your-event"
                            value={config.eventTicketUrl}
                            onChange={(e) => {
                              const newConfigs = [...videoConfigs];
                              newConfigs[index].eventTicketUrl = e.target.value;
                              setVideoConfigs(newConfigs);
                            }}
                            className="mt-2"
                          />
                        </div>
                      </div>
                    )}

                    {/* Other - Custom Goal */}
                    {config.goal === "other" && (
                      <div className="p-3 rounded-lg border border-neon-cyan/30 bg-neon-cyan/5 animate-fade-in">
                        <Label htmlFor={`custom-goal-${index}`} className="text-sm">
                          ✍️ Describe Your Goal
                          <InfoTooltip text="Tell us what you want to achieve with this video" />
                        </Label>
                        <Textarea
                          id={`custom-goal-${index}`}
                          placeholder="Describe what you want this video to achieve for your business..."
                          value={config.customGoal}
                          onChange={(e) => {
                            const newConfigs = [...videoConfigs];
                            newConfigs[index].customGoal = e.target.value;
                            setVideoConfigs(newConfigs);
                          }}
                          className="mt-2 min-h-[80px]"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Section 3: Where Are You Located? */}
            <section className={cn(
              "glass-morphism rounded-xl p-6 border-2 transition-all duration-500",
              "border-neon-purple/30 hover:border-neon-purple/50",
              "animate-section-reveal"
            )} style={{ animationDelay: "200ms" }}>
              <div className="flex items-center gap-3 mb-6">
                <AnimatedSectionBadge 
                  number={3} 
                  isComplete={!!googleMapsLink}
                />
                <h2 className="text-2xl font-heading font-bold bg-gradient-to-r from-white via-neon-purple to-white animate-text-shimmer bg-[length:200%_100%] bg-clip-text text-transparent">📍 Launch Coordinates</h2>
              </div>

              <div>
                <Label htmlFor="google-maps">
                  <Globe className="w-4 h-4 inline mr-2" />
                  Google Maps Link <span className="text-xs text-neon-cyan">(Optional)</span>
                  <InfoTooltip text="We'll tag your location in videos to boost local discovery and foot traffic" />
                </Label>
                <Input
                  id="google-maps"
                  type="url"
                  placeholder="https://maps.google.com/?q=..."
                  value={googleMapsLink}
                  onChange={(e) => setGoogleMapsLink(e.target.value)}
                  className="mt-2"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Paste your Google Maps link so we can tag your exact location
                </p>
              </div>
            </section>

            {/* Section 4: Assets & Branding */}
            <section className={cn(
              "glass-morphism rounded-xl p-6 border-2 transition-all duration-500",
              "border-neon-purple/30 hover:border-neon-purple/50",
              "animate-section-reveal"
            )} style={{ animationDelay: "300ms" }}>
              <div className="flex items-center gap-3 mb-4">
                <AnimatedSectionBadge 
                  number={4} 
                  isComplete={logoFiles.length > 0 || storePhotos.length > 0 || productImages.length > 0}
                />
                <h2 className="text-2xl font-heading font-bold bg-gradient-to-r from-white via-neon-purple to-white animate-text-shimmer bg-[length:200%_100%] bg-clip-text text-transparent">🎨 Your Rocket's Look</h2>
              </div>

              {/* Optional but Recommended Message */}
              <div className="mb-6 p-4 rounded-lg border border-neon-cyan/30 bg-neon-cyan/5">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <span className="text-neon-cyan font-semibold">✨ Optional but highly recommended:</span> Upload your logo, store photos (interior/exterior), and product images for best results. 
                  <span className="block mt-2">💡 Don't have them ready? We can extract assets from your website or create a professional logo for you (+€8).</span>
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Logo Upload */}
                <div className="p-6 rounded-xl bg-card/50 border border-border/40">
                  <Label className="flex items-center gap-2 mb-3">
                    <ImageIcon className="w-4 h-4" />
                    Logo <span className="text-xs text-neon-cyan">(Optional)</span>
                    <span className="text-xs text-muted-foreground ml-auto">{logoPreviews.length}/4</span>
                    <InfoTooltip text="Upload your brand logo (PNG with transparent background works best) or we can create one for you (+€8 add-on)" />
                  </Label>
                  {logoPreviews.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      {logoPreviews.map((preview, idx) => (
                        <div key={idx} className="relative group">
                          <img src={preview} alt={`Logo ${idx + 1}`} className="w-full h-16 object-contain rounded-lg bg-white/5" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx, "logo")}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {logoPreviews.length < 4 && (
                    <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-border/40 rounded-lg cursor-pointer hover:border-neon-purple/40 transition-colors">
                      <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                      <span className="text-xs text-muted-foreground">{logoPreviews.length === 0 ? "Upload Logo" : "Add more"}</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => handleMultiFileUpload(e.target.files, "logo")}
                      />
                    </label>
                  )}
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    We can extract from your site or create one
                  </p>
                </div>

                {/* Store Photo Upload */}
                <div className="p-6 rounded-xl bg-card/50 border border-border/40">
                  <Label className="flex items-center gap-2 mb-3">
                    <Store className="w-4 h-4" />
                    Store/Location <span className="text-xs text-neon-cyan">(Optional)</span>
                    <span className="text-xs text-muted-foreground ml-auto">{storePhotoPreviews.length}/4</span>
                    <InfoTooltip text="Photos of your physical space, storefront, interior, or team in action - greatly improves authenticity and local appeal" />
                  </Label>
                  {storePhotoPreviews.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      {storePhotoPreviews.map((preview, idx) => (
                        <div key={idx} className="relative group">
                          <img src={preview} alt={`Store ${idx + 1}`} className="w-full h-16 object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx, "store")}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {storePhotoPreviews.length < 4 && (
                    <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-border/40 rounded-lg cursor-pointer hover:border-neon-purple/40 transition-colors">
                      <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                      <span className="text-xs text-muted-foreground">{storePhotoPreviews.length === 0 ? "Upload Photo" : "Add more"}</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => handleMultiFileUpload(e.target.files, "store")}
                      />
                    </label>
                  )}
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Interior, exterior, or team shots
                  </p>
                </div>

                {/* Product Image Upload */}
                <div className="p-6 rounded-xl bg-card/50 border border-border/40">
                  <Label className="flex items-center gap-2 mb-3">
                    <Package className="w-4 h-4" />
                    Product <span className="text-xs text-neon-cyan">(Optional)</span>
                    <span className="text-xs text-muted-foreground ml-auto">{productImagePreviews.length}/4</span>
                    <InfoTooltip text="Hero shots of your main products or services - helps us showcase what you sell effectively" />
                  </Label>
                  {productImagePreviews.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      {productImagePreviews.map((preview, idx) => (
                        <div key={idx} className="relative group">
                          <img src={preview} alt={`Product ${idx + 1}`} className="w-full h-16 object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx, "product")}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {productImagePreviews.length < 4 && (
                    <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-border/40 rounded-lg cursor-pointer hover:border-neon-purple/40 transition-colors">
                      <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                      <span className="text-xs text-muted-foreground">{productImagePreviews.length === 0 ? "Upload Product" : "Add more"}</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => handleMultiFileUpload(e.target.files, "product")}
                      />
                    </label>
                  )}
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Best-selling items or services
                  </p>
                </div>
              </div>

              {/* Reference URL */}
              <div className="p-6 rounded-xl bg-card/50 border border-border/40">
                <Label htmlFor="reference-url">
                  <LinkIcon className="w-4 h-4 inline mr-2" />
                  Reference URL (Optional)
                </Label>
                <Input
                  id="reference-url"
                  type="url"
                  placeholder="https://example.com/inspiration"
                  value={referenceUrl}
                  onChange={(e) => setReferenceUrl(e.target.value)}
                  className="mt-2"
                />
              </div>

              {/* Brand Color */}
              <div className="p-6 rounded-xl bg-card/50 border border-border/40">
                <Label htmlFor="brand-color">
                  <Palette className="w-4 h-4 inline mr-2" />
                  Brand Color <span className="text-xs text-neon-cyan">(Optional)</span>
                  <InfoTooltip text="Your primary brand color for text overlays and graphic elements" />
                </Label>
                <div className="flex items-center gap-3 mt-2">
                  <input
                    id="brand-color"
                    type="color"
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="h-10 w-20 rounded cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>

              {/* Business Website */}
              <div className="p-6 rounded-xl bg-card/50 border border-border/40">
                <Label htmlFor="business-website">
                  <Globe className="w-4 h-4 inline mr-2" />
                  Business Website <span className="text-xs text-neon-cyan">(Optional)</span>
                  <InfoTooltip text="Your business website - we can extract branding assets and understand your brand better" />
                </Label>
                <Input
                  id="business-website"
                  type="url"
                  placeholder="https://yourbusiness.com"
                  value={businessWebsite}
                  onChange={(e) => setBusinessWebsite(e.target.value)}
                  className="mt-2"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  We can extract your logo, colors, and brand style from here
                </p>
              </div>

              {/* Social Media Handles */}
              <div className="p-6 rounded-xl bg-card/50 border border-border/40">
                <Label htmlFor="social-media">
                  <AtSign className="w-4 h-4 inline mr-2" />
                  Social Media Handles <span className="text-xs text-neon-cyan">(Optional)</span>
                  <InfoTooltip text="Your TikTok, Instagram handles - we'll tag you in videos and match your content style" />
                </Label>
                <Input
                  id="social-media"
                  type="text"
                  placeholder="@tiktok_handle, @instagram_handle"
                  value={socialMedia}
                  onChange={(e) => setSocialMedia(e.target.value)}
                  className="mt-2"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  TikTok, Instagram handles (comma-separated)
                </p>
              </div>
            </section>

            {/* Section 5: Business Context */}
            <section className={cn(
              "glass-morphism rounded-xl p-6 border-2 transition-all duration-500",
              "border-neon-purple/30 hover:border-neon-purple/50",
              "animate-section-reveal"
            )} style={{ animationDelay: "400ms" }}>
              <div className="flex items-center gap-3 mb-6">
                <AnimatedSectionBadge 
                  number={5} 
                  isComplete={!!businessName && !!city}
                />
                <h2 className="text-2xl font-heading font-bold bg-gradient-to-r from-white via-neon-purple to-white animate-text-shimmer bg-[length:200%_100%] bg-clip-text text-transparent">🚀 Your Mission</h2>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="business-name">Business Name *</Label>
                    <Input
                      id="business-name"
                      placeholder="Your business name"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      placeholder="Berlin"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <Label className="text-white/90 text-lg mb-4 block">
                      Business Type <span className="text-xs text-neon-cyan">(Optional)</span>
                      <InfoTooltip text="Helps us tailor the video style and messaging to your business model" />
                    </Label>
                    <RadioGroup value={businessType} onValueChange={setBusinessType} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Label
                        htmlFor="type-ecommerce"
                        className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all backdrop-blur-sm ${
                          businessType === "ecommerce"
                            ? "border-neon-purple bg-white/10 shadow-lg shadow-neon-purple/20"
                            : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/8"
                        }`}
                      >
                        <RadioGroupItem value="ecommerce" id="type-ecommerce" className="mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">🛒</span>
                            <span className="font-semibold text-white">E-commerce</span>
                          </div>
                          <p className="text-sm text-white/60">You sell products online</p>
                        </div>
                      </Label>

                      <Label
                        htmlFor="type-local"
                        className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all backdrop-blur-sm ${
                          businessType === "local"
                            ? "border-neon-purple bg-white/10 shadow-lg shadow-neon-purple/20"
                            : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/8"
                        }`}
                      >
                        <RadioGroupItem value="local" id="type-local" className="mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">🏪</span>
                            <span className="font-semibold text-white">Local Business</span>
                          </div>
                          <p className="text-sm text-white/60">Physical location customers visit</p>
                        </div>
                      </Label>

                      <Label
                        htmlFor="type-saas"
                        className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all backdrop-blur-sm ${
                          businessType === "saas"
                            ? "border-neon-purple bg-white/10 shadow-lg shadow-neon-purple/20"
                            : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/8"
                        }`}
                      >
                        <RadioGroupItem value="saas" id="type-saas" className="mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">💼</span>
                            <span className="font-semibold text-white">B2B / SaaS</span>
                          </div>
                          <p className="text-sm text-white/60">Business services or software</p>
                        </div>
                      </Label>

                      <Label
                        htmlFor="type-creator"
                        className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all backdrop-blur-sm ${
                          businessType === "creator"
                            ? "border-neon-purple bg-white/10 shadow-lg shadow-neon-purple/20"
                            : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/8"
                        }`}
                      >
                        <RadioGroupItem value="creator" id="type-creator" className="mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">👤</span>
                            <span className="font-semibold text-white">Creator / Personal Brand</span>
                          </div>
                          <p className="text-sm text-white/60">Personal content or coaching</p>
                        </div>
                      </Label>
                    </RadioGroup>
                  </div>

                  <div>
                    <Label className="text-white/90 text-lg mb-4 block">
                      Target Audience <span className="text-xs text-neon-cyan">(Optional)</span>
                      <InfoTooltip text="We'll match the tone, trends, and style your audience loves" />
                    </Label>
                    <RadioGroup value={targetAudience} onValueChange={setTargetAudience} className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <Label
                        htmlFor="age-18-30"
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 cursor-pointer transition-all backdrop-blur-sm ${
                          targetAudience === "18-30"
                            ? "border-neon-cyan bg-white/10 shadow-lg shadow-neon-cyan/20"
                            : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/8"
                        }`}
                      >
                        <RadioGroupItem value="18-30" id="age-18-30" className="sr-only" />
                        <span className="text-sm font-semibold text-white text-center">18-30 years</span>
                        <span className="text-xs text-white/60">Gen Z</span>
                      </Label>

                      <Label
                        htmlFor="age-30-50"
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 cursor-pointer transition-all backdrop-blur-sm ${
                          targetAudience === "30-50"
                            ? "border-neon-cyan bg-white/10 shadow-lg shadow-neon-cyan/20"
                            : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/8"
                        }`}
                      >
                        <RadioGroupItem value="30-50" id="age-30-50" className="sr-only" />
                        <span className="text-sm font-semibold text-white text-center">30-50 years</span>
                        <span className="text-xs text-white/60">Millennials</span>
                      </Label>

                      <Label
                        htmlFor="age-50-plus"
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 cursor-pointer transition-all backdrop-blur-sm ${
                          targetAudience === "50+"
                            ? "border-neon-cyan bg-white/10 shadow-lg shadow-neon-cyan/20"
                            : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/8"
                        }`}
                      >
                        <RadioGroupItem value="50+" id="age-50-plus" className="sr-only" />
                        <span className="text-sm font-semibold text-white text-center">50+ years</span>
                        <span className="text-xs text-white/60">Boomers+</span>
                      </Label>

                      <Label
                        htmlFor="age-all"
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 cursor-pointer transition-all backdrop-blur-sm ${
                          targetAudience === "All ages"
                            ? "border-neon-cyan bg-white/10 shadow-lg shadow-neon-cyan/20"
                            : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/8"
                        }`}
                      >
                        <RadioGroupItem value="All ages" id="age-all" className="sr-only" />
                        <span className="text-sm font-semibold text-white text-center">All ages</span>
                        <span className="text-xs text-white/60">Universal</span>
                      </Label>
                    </RadioGroup>
                  </div>
                </div>

                <div>
                  <Label>
                    Platforms <span className="text-xs text-neon-cyan">(Optional)</span>
                    <InfoTooltip text="Select all platforms where you'll share these videos" />
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                    {["tiktok", "instagram", "facebook", "youtube", "linkedin", "whatsapp"].map((platform) => (
                      <div key={platform} className="flex items-center space-x-2">
                        <Checkbox
                          id={`platform-${platform}`}
                          checked={platforms.includes(platform)}
                          onCheckedChange={() => handlePlatformToggle(platform)}
                        />
                        <Label htmlFor={`platform-${platform}`} className="capitalize cursor-pointer">
                          {platform}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="call-to-action">
                    Call to Action <span className="text-xs text-neon-cyan">(Optional)</span>
                    <InfoTooltip text="What action should viewers take after watching? (e.g., DM us, Visit our site, Book now)" />
                  </Label>
                  <Input
                    id="call-to-action"
                    placeholder="Book now, Visit us, Shop now, etc."
                    value={callToAction}
                    onChange={(e) => setCallToAction(e.target.value)}
                  />
                </div>
              </div>
            </section>

            {/* Section 6: Final Notes */}
            <section className={cn(
              "glass-morphism rounded-xl p-6 border-2 transition-all duration-500",
              "border-neon-purple/30 hover:border-neon-purple/50",
              "animate-section-reveal"
            )} style={{ animationDelay: "500ms" }}>
              <div className="flex items-center gap-3 mb-6">
                <AnimatedSectionBadge number={6} isComplete={!!creativeGoal || !!vibe} />
                <h2 className="text-2xl font-heading font-bold bg-gradient-to-r from-white via-neon-purple to-white animate-text-shimmer bg-[length:200%_100%] bg-clip-text text-transparent">💬 Final Pre-Flight Check</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="inspiration">
                    Extra Notes (Optional)
                    <InfoTooltip text="Anything else we should know? Links, inspiration, do's & don'ts..." />
                  </Label>
                  <Textarea
                    id="inspiration"
                    placeholder="Share any additional details, links to videos you like, what to avoid, special requests..."
                    value={inspiration}
                    onChange={(e) => setInspiration(e.target.value)}
                    rows={4}
                  />
                </div>
              </div>

              {/* Voice Note Section */}
              <div className="p-6 rounded-xl bg-card/50 border border-border/40 space-y-4">
                <div className="flex items-center gap-2">
                  <Mic className="w-5 h-5 text-neon-purple" />
                  <Label>
                    Voice Note (Optional)
                    <InfoTooltip text="Sometimes it's easier to explain your vision - we love voice notes!" />
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Leave us a voice note explaining your vision (max 60 seconds)
                </p>

                <div className="space-y-4">
                  {!voiceNoteFile && !isRecording && (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        type="button"
                        onClick={startRecording}
                        variant="outline"
                        className="flex-1"
                      >
                        <Mic className="w-4 h-4 mr-2" />
                        Start Recording
                      </Button>
                      <div className="flex-1">
                        <Input
                          type="file"
                          accept="audio/*"
                          onChange={handleVoiceNoteUpload}
                          className="cursor-pointer"
                        />
                      </div>
                    </div>
                  )}

                  {isRecording && (
                    <div className="p-4 rounded-lg bg-neon-purple/10 border border-neon-purple/40">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                          <span className="font-mono text-lg">{formatTime(recordingTime)}</span>
                          <span className="text-sm text-muted-foreground">/ 1:00</span>
                        </div>
                        <Button
                          type="button"
                          onClick={stopRecording}
                          size="sm"
                          variant="destructive"
                        >
                          <Square className="w-4 h-4 mr-2" />
                          Stop
                        </Button>
                      </div>
                    </div>
                  )}

                  {voiceNoteFile && !isRecording && (
                    <div className="p-4 rounded-lg bg-neon-cyan/10 border border-neon-cyan/40">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-neon-cyan" />
                          <div>
                            <p className="font-semibold">Voice note recorded</p>
                            <p className="text-xs text-muted-foreground">{voiceNoteFile.name}</p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          onClick={() => {
                            setVoiceNoteFile(null);
                            setAudioBlob(null);
                          }}
                          size="sm"
                          variant="ghost"
                        >
                          Remove
                        </Button>
                      </div>
                      {audioBlob && (
                        <audio controls className="w-full mt-3">
                          <source src={URL.createObjectURL(audioBlob)} type="audio/webm" />
                        </audio>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Section 7: Contact & Delivery */}
            <section className={cn(
              "glass-morphism rounded-xl p-6 border-2 transition-all duration-500",
              "border-neon-purple/30 hover:border-neon-purple/50",
              "animate-section-reveal"
            )} style={{ animationDelay: "600ms" }}>
              <div className="flex items-center gap-3 mb-6">
                <AnimatedSectionBadge number={7} isComplete={!!email && !!whatsapp && agreedToTerms} />
                <h2 className="text-2xl font-heading font-bold bg-gradient-to-r from-white via-neon-purple to-white animate-text-shimmer bg-[length:200%_100%] bg-clip-text text-transparent">🔥 Ignition Details</h2>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email *
                      <InfoTooltip text="We'll send your order confirmation and updates here" />
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="whatsapp">
                      <Phone className="w-4 h-4 inline mr-2" />
                      WhatsApp *
                      <InfoTooltip text="We'll send delivery updates and final files directly to WhatsApp" />
                    </Label>
                    <Input
                      id="whatsapp"
                      type="tel"
                      placeholder="+49 123 456789"
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Delivery Preference</Label>
                  <RadioGroup value={deliveryPreference} onValueChange={setDeliveryPreference}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      <div className="flex items-center space-x-3 p-4 rounded-lg border border-border/40">
                        <RadioGroupItem value="standard" id="delivery-standard" />
                        <Label htmlFor="delivery-standard" className="cursor-pointer flex-1">
                          <span className="font-semibold">Standard (72h)</span>
                          <p className="text-xs text-muted-foreground">Free delivery</p>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3 p-4 rounded-lg border border-border/40">
                        <RadioGroupItem value="express" id="delivery-express" />
                        <Label htmlFor="delivery-express" className="cursor-pointer flex-1">
                          <span className="font-semibold">
                            <Zap className="w-4 h-4 inline mr-1 text-neon-pink" />
                            Express (24h) +€49
                          </span>
                          <p className="text-xs text-muted-foreground">Priority delivery</p>
                        </Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                <div>
                  <Label>Optional Add-ons</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    {addOns.map((addOn) => {
                      const addOnIcons: Record<string, string> = {
                        extra_posters: "🖼️",
                        whatsapp_support: "💬",
                        logo_creation: "🎨"
                      };
                      const addOnDescriptions: Record<string, string> = {
                        extra_posters: "3 additional poster designs",
                        whatsapp_support: "Direct access for quick updates",
                        logo_creation: "Professional logo designed for you"
                      };

                      const isLogoAutoAdded = addOn.id === "logo_creation" && logoFiles.length === 0;
                      
                      return (
                        <div key={addOn.id} className={`flex items-start space-x-3 p-4 rounded-lg border transition-colors ${
                          isLogoAutoAdded ? "border-neon-cyan/50 bg-neon-cyan/5" : "border-border/40 hover:border-neon-purple/40"
                        }`}>
                          <Checkbox
                            id={`addon-${addOn.id}`}
                            checked={selectedAddOns.includes(addOn.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedAddOns([...selectedAddOns, addOn.id]);
                              } else {
                                setSelectedAddOns(selectedAddOns.filter((id) => id !== addOn.id));
                              }
                            }}
                            className="mt-1"
                          />
                          <Label htmlFor={`addon-${addOn.id}`} className="cursor-pointer flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">{addOnIcons[addOn.id]}</span>
                              <span className="font-semibold">{addOn.name}</span>
                              {isLogoAutoAdded && (
                                <span className="text-xs text-neon-cyan">(auto-added - no logo uploaded)</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{addOnDescriptions[addOn.id]}</p>
                            <span className="text-sm text-neon-cyan font-semibold mt-1 inline-block">+€{addOn.price}</span>
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Custom Notes */}
              <div className="mt-4 space-y-4">
                <Label htmlFor="custom-notes">
                  <FileText className="w-4 h-4 inline mr-2" />
                  Custom Notes (Optional)
                  <InfoTooltip text="Anything else we should know about your project or preferences?" />
                </Label>
                <Textarea
                  id="custom-notes"
                  placeholder="Any additional information or special requests..."
                  value={customNotes}
                  onChange={(e) => setCustomNotes(e.target.value)}
                  rows={4}
                />
              </div>

              {/* Terms */}
              <div className="flex items-center space-x-2 mt-4">
                <Checkbox
                  id="terms"
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
                />
                <Label htmlFor="terms" className="text-sm cursor-pointer">
                  I agree to the terms and conditions and {deliveryPreference === "express" ? "24-hour" : "72-hour"} delivery timeline
                </Label>
              </div>
            </section>

            {/* Submit Button */}
            <PremiumSubmitButton
              onClick={handleSubmit}
              isSubmitting={isSubmitting}
              isDisabled={!agreedToTerms}
              total={calculateTotal()}
              isFormComplete={getCompletedSections() >= 6}
            />
          </div>

          {/* Order Summary Sidebar - Properly sticky on desktop */}
          <div className="lg:col-span-1 lg:self-start">
            <div className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-120px)]">
              <OrderSummary
                selectedPack={selectedPack}
                selectedAddOns={selectedAddOns}
                addOns={addOns}
                total={calculateTotal()}
                deliveryPreference={deliveryPreference as "standard" | "express"}
                completedSections={getCompletedSections()}
                totalSections={7}
              />
            </div>
          </div>
        </div>
      </div>

        <Footer />
      </div>
    </TooltipProvider>
  );
};

export default Order;
